<!-- Generated by Copilot -->
<template>
  <v-app>
    <v-app-bar color="primary" density="compact">
      <v-app-bar-title>TEMPO Data Service Testing Interface</v-app-bar-title>
      <v-spacer></v-spacer>
      <v-btn icon="mdi-help-circle" @click="showHelp = true"></v-btn>
    </v-app-bar>

    <v-main>
      <v-container fluid class="pa-4">
        <v-row>
          <!-- Left Panel: Map and Controls -->
          <v-col cols="12" lg="7">
            <!-- Step 1: Region Selection -->
            <v-card class="mb-4">
              <v-card-title class="d-flex align-center">
                <v-chip color="primary" variant="flat" size="small" class="mr-2">STEP 1</v-chip>
                Select Test Region
                <v-spacer></v-spacer>
                <info-button tooltip-text="Learn about test regions">
                  <p><strong>Test Regions</strong></p>
                  <p>Select a geographic area to analyze. Region size affects data volume:</p>
                  <ul>
                    <li><strong>Small:</strong> Fast testing, minimal data</li>
                    <li><strong>Medium:</strong> Balanced data volume</li>
                    <li><strong>Large:</strong> Performance stress testing</li>
                  </ul>
                  <p><strong>Sample Count:</strong> Number of sampling points across the region. More points = better spatial resolution but larger datasets.</p>
                </info-button>
              </v-card-title>
              <v-card-text>
                <!-- Instructional Alert -->
                <v-alert
                  v-if="!selectedRegion"
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="mb-3"
                  icon="mdi-cursor-default-click"
                >
                  <strong>Select a region:</strong> Click a chip below to choose your study area
                </v-alert>
                
                <!-- Current Map Timestamp Display -->
                <div v-if="mapTimestamp" class="mb-2 pa-2 bg-blue-grey-lighten-5 rounded text-center">
                  <strong>Current Map Time (UTC):</strong> 
                  <span class="text-primary">{{ new Date(mapTimestamp).toLocaleString('en-US', { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'medium' }) }}</span>
                </div>
                
                <v-row>
                  <!-- Map Column -->
                  <v-col cols="12" md="7">
                    <div class="map-container" style="height: 400px;">
                      <esri-map
                        :mapID="'test-map'"
                        :home="homeState"
                        :initial="homeState"
                        :show-roads="true"
                        :molecule="molecule"
                        :timestamp="mapTimestamp"
                        :opacity="mapOpacity"
                        :show-field-of-regard="false"
                        @ready="onMapReady"
                      >
                      </esri-map>
                    </div>
                  </v-col>
                  
                  <!-- Region Selection Column -->
                  <v-col cols="12" md="5">
                    <v-card variant="outlined" class="pa-3">
                      <div class="d-flex align-center mb-2">
                        <h4>Available Regions</h4>
                      </div>
                      <v-chip-group column mandatory>
                        <v-chip
                          v-for="region in predefinedRegions"
                          :key="region.id"
                          :color="region.color"
                          variant="outlined"
                          @click="selectRegion(region)"
                          :class="{ 'v-chip--active': selectedRegion?.id === region.id }"
                        >
                          {{ region.name }} ({{ region.size }})
                        </v-chip>
                      </v-chip-group>
                      <div v-if="selectedRegion" class="mt-2 text-caption">
                        <strong>Selected:</strong> {{ selectedRegion.name }}
                        <br />
                        Bounds: {{ selectedRegion.geometry.xmin.toFixed(3) }}, 
                        {{ selectedRegion.geometry.ymin.toFixed(3) }} to
                        {{ selectedRegion.geometry.xmax.toFixed(3) }}, 
                        {{ selectedRegion.geometry.ymax.toFixed(3) }}
                      </div>
                      
                      <!-- Sample Count Configuration -->
                      <v-divider class="my-3"></v-divider>
                      <h4 class="mb-2">Spatial Sampling</h4>
                      <v-text-field
                        v-model.number="sampleCount"
                        label="Sample Count"
                        type="number"
                        min="1"
                        max="100"
                        density="compact"
                        hint="Sampling points across region (1-100). More points = better coverage."
                        persistent-hint
                      ></v-text-field>
                      
                      <!-- Sample Count Adjustment Info -->
                      <v-alert
                        v-if="sampleCountAdjustmentInfo"
                        :type="sampleCountAdjustmentInfo.type === 'reduced' ? 'warning' : 'info'"
                        density="compact"
                        variant="tonal"
                        class="mt-2"
                      >
                        <div class="text-caption">
                          <strong>Actual: {{ actualSampleCount }} locations</strong>
                          <br />
                          {{ sampleCountAdjustmentInfo.message }}
                        </div>
                      </v-alert>
                      
                      <!-- Cache Busting -->
                      <v-divider class="my-3"></v-divider>
                      <v-btn
                        block
                        size="small"
                        variant="outlined"
                        color="warning"
                        @click="jitterRegions"
                      >
                        <v-icon start>mdi-shuffle-variant</v-icon>
                        Randomize Regions
                      </v-btn>
                      <div class="text-caption mt-1 text-grey">
                        Slightly shifts region boundaries to bypass server caching
                      </div>
                      
                      <!-- Show Sample Points Toggle -->
                      <v-divider class="my-3"></v-divider>
                      <details>
                        <summary class="cursor-pointer text-body-2 font-weight-bold mb-2" style="cursor: pointer;">
                          Display Sample Points
                        </summary>
                        <div class="mt-2">
                          <div class="text-caption font-weight-bold mb-1">Actual Samples (Red):</div>
                          <v-list density="compact" class="pa-0">
                            <v-list-item
                              v-for="region in predefinedRegions"
                              :key="`actual-samples-${region.id}`"
                              class="px-0"
                              :disabled="!regionActualSampleLocations[region.id] || regionActualSampleLocations[region.id].length === 0"
                            >
                              <template #prepend>
                                <v-checkbox
                                  v-model="showActualSamplePoints[region.id]"
                                  color="red"
                                  hide-details
                                  density="compact"
                                  :disabled="!regionActualSampleLocations[region.id] || regionActualSampleLocations[region.id].length === 0"
                                  @change="toggleActualSamplePoints(region)"
                                ></v-checkbox>
                              </template>
                              <v-list-item-title class="text-body-2">
                                {{ region.name }}
                                <span v-if="regionActualSampleLocations[region.id]" class="text-caption text-grey">
                                  ({{ regionActualSampleLocations[region.id].length }} points)
                                </span>
                              </v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </div>
                      </details>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Time Range Selection & Fetch Controls Combined -->
            <v-card class="mb-4">
              <v-card-title class="d-flex align-center">
                <v-chip color="primary" variant="flat" size="small" class="mr-2">STEP 2</v-chip>
                Select Time Range
                <v-spacer></v-spacer>
                <info-button tooltip-text="Learn about time ranges">
                  <p><strong>Time Range Selection</strong></p>
                  <p><strong>Presets:</strong> Ready-to-use time periods for common scenarios</p>
                  <p><strong>Custom:</strong> Build your own ranges with specific dates, times, or patterns</p>
                  <p><strong>Tip:</strong> More time ranges = more requests. Use "Dry Run" to preview first.</p>
                </info-button>
              </v-card-title>
              <v-card-text>
                <!-- Instructional Alert -->
                <v-alert
                  v-if="!selectedTimeRange"
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="mb-3"
                  icon="mdi-calendar-clock"
                >
                  <strong>Choose a time period:</strong> Select a preset or create your own
                </v-alert>
                
                <v-row>
                  <!-- Time Range Selection -->
                  <v-col cols="12" md="6">
                    <v-tabs v-model="timeRangeTab" density="compact">
                      <v-tab value="presets">Presets</v-tab>
                      <v-tab value="custom">Custom</v-tab>
                    </v-tabs>

                    <v-window v-model="timeRangeTab" class="mt-3" eager>
                      <!-- Preset Time Ranges -->
                      <v-window-item value="presets" eager>
                        <v-table density="compact" class="text-caption">
                          <thead>
                            <tr>
                              <th class="text-left">Preset</th>
                              <th class="text-left">Description</th>
                              <th class="text-center"># of Distinct Time Ranges</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="preset in allTimeRanges"
                              :key="preset.id"
                              @click="selectTimeRange(preset)"
                              :class="{ 'bg-primary-lighten-4': selectedTimeRange?.id === preset.id }"
                              style="cursor: pointer;"
                            >
                              <td class="font-weight-bold">
                                <v-icon 
                                  v-if="selectedTimeRange?.id === preset.id" 
                                  size="small" 
                                  color="primary"
                                  start
                                >
                                  mdi-check-circle
                                </v-icon>
                                {{ preset.name }}
                              </td>
                              <td>{{ preset.description }}</td>
                              <td class="text-center">
                                <v-chip size="x-small" color="info" variant="flat">
                                  {{ preset.ranges.length }}
                                </v-chip>
                              </td>
                            </tr>
                          </tbody>
                        </v-table>
                      </v-window-item>

                      <!-- Custom Time Range -->
                      <v-window-item value="custom">
                        <date-time-range-selection
                          :key="dateTimeKey"
                          :current-date="currentDate"
                          :allowed-dates="uniqueDays"
                          @ranges-change="handleCustomTimeRangeChange"
                        />
                        
                        <!-- Display created custom ranges -->
                        <div v-if="customTimeRanges.length > 0" class="mt-3">
                          <v-divider class="mb-2"></v-divider>
                          <div class="text-caption font-weight-bold mb-2">Custom Ranges:</div>
                          <v-chip-group column>
                            <v-chip
                              v-for="custom in customTimeRanges"
                              :key="custom.id"
                              variant="outlined"
                              size="small"
                              closable
                              @click="selectTimeRange(custom)"
                              @click:close="removeCustomTimeRange(custom.id)"
                              :class="{ 'v-chip--active': selectedTimeRange?.id === custom.id }"
                            >
                              {{ custom.name }}
                            </v-chip>
                          </v-chip-group>
                        </div>
                      </v-window-item>
                    </v-window>
                  </v-col>
                  
                  <!-- Fetch Controls -->
                  <v-col cols="12" md="6">
                    <div class="d-flex align-center mb-2">
                      <v-chip color="primary" variant="flat" size="small" class="mr-2">STEP 3</v-chip>
                      <h4>Configure & Fetch</h4>
                      <v-spacer></v-spacer>
                      <info-button tooltip-text="Learn about fetch settings">
                        <p><strong>Parceling:</strong> How to split time ranges into requests</p>
                        <ul>
                          <li><strong>None:</strong> Single requests (fast but may fail on large ranges)</li>
                          <li><strong>Default:</strong> Fixed-size chunks (predictable, configurable)</li>
                          <li><strong>Smart:</strong> Optimized batches (most efficient, recommended)</li>
                        </ul>
                        <p><strong>Rate Limiting:</strong> Delay between requests to prevent server overload.</p>
                      </info-button>
                    </div>
                    
                    <!-- Parceling Configuration (First) -->
                    <v-card variant="outlined" class="pa-2 mb-2">
                      <div class="text-caption font-weight-bold mb-2">1. Parceling Strategy</div>
                      <v-alert
                        type="info"
                        variant="tonal"
                        density="compact"
                        class="mb-2 text-caption"
                      >
                        <strong>Purpose:</strong> Break large time ranges into manageable requests
                      </v-alert>
                      
                      <v-select
                        v-model="parcelingMode"
                        label="Parceling Mode"
                        :items="parcelingModeOptions"
                        density="compact"
                        class="mb-2"
                      ></v-select>
                      
                      <!-- Explanation for selected mode -->
                      <v-alert
                        v-if="parcelingMode === 'none'"
                        type="info"
                        variant="outlined"
                        density="compact"
                        class="mb-2 text-caption"
                      >
                        <strong>None (As-Is):</strong> Sends one request per time range without splitting. Fast for short ranges, but may fail if a single range exceeds server limits (typically ~2000 samples = locations × timestamps).
                      </v-alert>
                      
                      <v-alert
                        v-if="parcelingMode === 'default'"
                        type="info"
                        variant="outlined"
                        density="compact"
                        class="mb-2 text-caption"
                      >
                        <strong>Default (Fixed Chunks):</strong> Splits time ranges into fixed-size chunks based on days. Predictable and configurable. Good when you want consistent request sizes regardless of data density.
                      </v-alert>
                      
                      <v-alert
                        v-if="parcelingMode === 'smart'"
                        type="success"
                        variant="outlined"
                        density="compact"
                        class="mb-2 text-caption"
                      >
                        <strong>Smart (Recommended):</strong> Analyzes available timestamps and creates optimally-sized requests that maximize data per request while staying under server limits. Most efficient for long time ranges with sparse data.
                      </v-alert>
                      
                      <!-- Default Mode Settings -->
                      <v-expand-transition>
                        <div v-if="parcelingMode === 'default'">
                          <div class="text-caption text-medium-emphasis mb-2">
                            Each time range will be split into chunks of the specified number of days.
                          </div>
                          <v-text-field
                            v-model.number="defaultParcelSizeDays"
                            label="Chunk Size (days)"
                            type="number"
                            min="1"
                            max="365"
                            density="compact"
                            hint="Days per request. Smaller = more requests but safer."
                            persistent-hint
                          ></v-text-field>
                          <div class="text-caption text-medium-emphasis mt-1">
                            <strong>Example:</strong> A 30-day range with 7-day chunks = ~5 requests
                          </div>
                        </div>
                      </v-expand-transition>
                      
                      <!-- Smart Mode Settings -->
                      <v-expand-transition>
                        <div v-if="parcelingMode === 'smart'">
                          <div class="text-caption text-medium-emphasis mb-2">
                            Smart mode groups timestamps to maximize efficiency while respecting server limits.
                          </div>
                          <v-row dense>
                            <v-col cols="6">
                              <v-text-field
                                v-model.number="maxSamplesPerRequest"
                                label="Max Samples/Request"
                                type="number"
                                min="100"
                                max="10000"
                                density="compact"
                                hint="Server limit (default: 2000)"
                                persistent-hint
                              ></v-text-field>
                            </v-col>
                            <v-col cols="6">
                              <v-text-field
                                v-model.number="safetyMargin"
                                label="Safety Margin"
                                type="number"
                                min="0.1"
                                max="1.0"
                                step="0.05"
                                density="compact"
                                hint="Use % of max (0.9 = 90%)"
                                persistent-hint
                              ></v-text-field>
                            </v-col>
                          </v-row>
                          <div class="text-caption text-medium-emphasis mt-2">
                            <strong>How it works:</strong> Calculates samples/request = locations × timestamps. Groups consecutive timestamps into batches that stay under (Max × Safety Margin). Minimizes total requests.
                          </div>
                        </div>
                      </v-expand-transition>
                    </v-card>
                    
                    <!-- Rate Limiting (Separate Section) -->
                    <v-card variant="outlined" class="pa-2 mb-2">
                      <div class="text-caption font-weight-bold mb-2">2. Rate Limiting & Retries</div>
                      <v-alert
                        type="info"
                        variant="tonal"
                        density="compact"
                        class="mb-2 text-caption"
                      >
                        <strong>Purpose:</strong> Pace requests to avoid overwhelming the server
                      </v-alert>
                      <div class="text-caption text-medium-emphasis mb-2">
                        Controls the delay between consecutive requests and how many times to retry on server errors.
                      </div>
                      <v-row dense>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="rateLimitMs"
                            label="Request Delay (ms)"
                            type="number"
                            min="0"
                            max="100"
                            density="compact"
                            hint="Wait time. 0=fastest, 50=safe"
                            persistent-hint
                          ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="maxRetries503"
                            label="Server Busy Retries"
                            type="number"
                            min="0"
                            max="5"
                            density="compact"
                            hint="Attempts on 503 errors"
                            persistent-hint
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <div class="text-caption text-medium-emphasis mt-2">
                        <strong>Request Delay:</strong> 0ms = fastest but risky | 50-100ms = balanced | 200ms+ = very safe but slow<br>
                        <strong>503 Retries:</strong> Server may temporarily reject requests when overloaded. Retries help complete the request.
                      </div>
                    </v-card>
                    
                    <!-- Fetch Readiness Check -->
                    <v-alert
                      v-if="!canFetch"
                      type="warning"
                      variant="tonal"
                      density="compact"
                      class="mt-3"
                      icon="mdi-alert-circle"
                    >
                      <span class="text-caption">
                        <strong>Not ready:</strong> 
                        <span v-if="!selectedRegion">Select a region (Step 1)</span>
                        <span v-else-if="!selectedTimeRange">Select a time range (Step 2)</span>
                      </span>
                    </v-alert>
                    
                    <v-alert
                      v-else
                      type="success"
                      variant="tonal"
                      density="compact"
                      class="mt-3"
                      icon="mdi-check-circle"
                    >
                      <span class="text-caption"><strong>Ready!</strong> Fetch data or preview URLs with Dry Run</span>
                    </v-alert>
                    
                    <v-btn
                      color="primary"
                      size="large"
                      block
                      class="mt-2"
                      :loading="fetching"
                      :disabled="!canFetch"
                      @click="fetchData"
                      prepend-icon="mdi-download"
                    >
                      Fetch Data
                    </v-btn>
                    <v-btn
                      color="secondary"
                      variant="outlined"
                      block
                      class="mt-2"
                      :loading="fetching"
                      :disabled="!canFetch"
                      @click="dryRunData"
                      prepend-icon="mdi-test-tube"
                    >
                      Dry Run (Preview URLs)
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Right Panel: Graph and Statistics -->
          <v-col cols="12" lg="5">
            <!-- Progress indicator -->
            <v-card v-if="fetching" class="mb-3">
              <v-card-text class="py-2">
                <v-progress-linear
                  :model-value="fetchProgress"
                  color="primary"
                  height="20"
                  striped
                >
                  <template v-slot:default="{ value }">
                    <strong class="text-caption">{{ Math.ceil(value) }}%</strong>
                  </template>
                </v-progress-linear>
                <div class="text-caption text-center mt-1">
                  {{ completedRequests }} / {{ totalRequests }} requests
                </div>
              </v-card-text>
            </v-card>

            <!-- Graph -->
            <v-card class="mb-3">
              <v-card-title class="py-2">Time Series Data</v-card-title>
              <v-card-text class="pa-2">
                <plotly-graph
                  v-if="datasets.length > 0"
                  :datasets="plotlyDatasets"
                  :colors="plotlyColors"
                  :show-errors="true"
                  :layout-options="{legend: {y:1.15, orientation:'h', bordercolor: '#ccc', borderwidth:1}, height: 300}"
                  @click="handleGraphClick"
                />
                <v-alert v-else type="info" variant="outlined" density="compact">
                  <strong>No data yet.</strong> Complete Steps 1-2 above, then click "Fetch Data".
                </v-alert>
              </v-card-text>
            </v-card>

            <!-- Statistics -->
            <request-statistics
              :stats="requestStats"
              @clear="clearStatistics"
            />
            
            <!-- Time Range Status List -->
            <time-range-status-list
              v-if="requestStats.length > 0"
              :stats="requestStats"
              :selected-stat="selectedTimeRangeForMap"
              :height="400"
              class="mt-3"
              @select="handleTimeRangeSelect"
            />
          </v-col>
        </v-row>
        
        <!-- Footer with Citation -->
        <v-row>
          <v-col cols="12">
            <div class="text-center py-4 text-caption bg-grey-darken-4 text-grey-lighten-2">
              Project specification by "Human-in-the-Loop" John Lewis 👨🏾. 
              Interface implementation by 
              <a href="https://github.com/features/copilot" target="_blank" class="text-grey-lighten-1 text-decoration-none">GitHub Copilot</a> 
              using 
              <a href="https://www.anthropic.com/claude/sonnet" target="_blank" class="text-grey-lighten-1 text-decoration-none">Claude 4.5 Sonnet</a> 🤖
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <!-- Help Dialog -->
    <v-dialog v-model="showHelp" max-width="700">
      <v-card>
        <v-card-title>TEMPO ESRI Service Testing Interface</v-card-title>
        <v-card-text>
          <h4>Purpose</h4>
          <p>
            Test and optimize TEMPO ESRI Image Service performance while staying under
            the 5000 sample-per-request limit. Features smart parceling to maximize
            data retrieval efficiency based on actual timestamp availability.
          </p>
          
          <h4>Test Regions</h4>
          <ul>
            <li><strong>Small:</strong> 0.02° × 0.02° - Minimal spatial coverage</li>
            <li><strong>Medium:</strong> 0.1° × 0.05° - Moderate spatial coverage</li>
            <li><strong>Large:</strong> 1.25° × 1.0° - Large spatial coverage</li>
          </ul>

          <h4>Parceling Modes</h4>
          <ul>
            <li><strong>None:</strong> Use time ranges as-is (no splitting)</li>
            <li><strong>Default:</strong> Fixed-size chunks based on configurable time period</li>
            <li><strong>Smart:</strong> Intelligently split ranges based on actual data availability using O(T+R) algorithm</li>
          </ul>

          <h4>How to Use</h4>
          <ol>
            <li>Select a test region or click on the map</li>
            <li>Choose a time range (preset or custom)</li>
            <li>Configure parceling mode and sample count</li>
            <li>Click "Fetch Data" to retrieve samples</li>
            <li>Review console output for comprehensive statistics</li>
            <li>Visualize results: time series graph, expected vs actual sample markers</li>
          </ol>

          <h4>Sample Count Adjustment</h4>
          <p>
            ESRI samples on a regular grid defined by TEMPO satellite data. If your
            requested sample count doesn't align with the grid spacing, it will be
            automatically adjusted to match what ESRI would return. A warning will show 
            the actual count and grid dimensions used (e.g., "5 × 6 = 30 samples"). 
            This adjustment produces the same result as the equivalent ESRI request.
          </p>

          <h4>Statistics & Visualization</h4>
          <p>
            <strong>Console Output:</strong> Check browser console for 6 sections of detailed
            statistics including timing, request success/failure rates, sample counts,
            and efficiency metrics.
          </p>
          <p>
            <strong>Sample Markers:</strong> Toggle "Expected Locations" (colored by region)
            and "Actual Samples" (red) to verify ESRI returns data where expected.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showHelp = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup lang="ts">
import { ref, computed, watch, type Ref } from 'vue';
import { v4 } from 'uuid';
import type { Map } from 'maplibre-gl';
import EsriMap from './components/EsriMap.vue';
import DateTimeRangeSelection from './date_time_range_selection/DateTimeRangeSelection.vue';
import PlotlyGraph from './components/plotly/PlotlyGraph.vue';
import RequestStatistics from './components/RequestStatistics.vue';
import TimeRangeStatusList from './components/TimeRangeStatusList.vue';
import InfoButton from './components/InfoButton.vue';
import { useTempoStore } from './stores/app';
import type { LatLngPair, UserDataset, PlotltGraphDataSet } from './types';
import type { RectBounds } from './esri/geometry';
import type { RequestStats, ParcelingMode } from './esri/services/TempoDataService';
import type { MoleculeType } from './esri/utils';
import { useMultiMarker } from './composables/maplibre/useMultiMarker';
import { EsriSampler } from './esri/services/sampling';
import { useEsriTimesteps } from './composables/useEsriTimesteps';
import { useUniqueTimeSelection } from './composables/useUniqueTimeSelection';
import { generateTimeRanges, type TimeRangeConfig, type TimeRangeConfigMultiple } from './date_time_range_selection/date_time_range_generators';
import type { MillisecondRange } from './types/datetime';
import { filterForAllowedDates } from './date_time_range_selection/filter_ranges';
import { nanmean } from './utils/array_operations/array_math';
import { userDatasetToPlotly } from './utils/data_converters';
// Store
const store = useTempoStore();

// Fetch ESRI timesteps directly (not from store)
const molecule = ref<MoleculeType>('no2');
const { esriTimesteps } = useEsriTimesteps(molecule, true);

// Use unique time selection composable
const {
  singleDateSelected,
  uniqueDays,
} = useUniqueTimeSelection(esriTimesteps);

// State
const showHelp = ref(false);
const map = ref<Map | null>(null);
const fetching = ref(false);
const requestStats = ref<RequestStats[]>([]);
const datasets = ref<UserDataset[]>([]);
const dateTimeKey = ref(0);

// ESRI Map state
const mapOpacity = ref(0.8);
const mapTimestamp = ref<number | null>(null);
const selectedTimeRangeForMap = ref<RequestStats | null>(null);

// Progress tracking
const totalRequests = ref(0);
const completedRequests = ref(0);
const fetchProgress = computed(() => 
  totalRequests.value > 0 ? (completedRequests.value / totalRequests.value) * 100 : 0
);

// Settings
const sampleCount = ref(30);
const rateLimitMs = ref(50);
const maxRetries503 = ref(1);

// Parceling configuration
const parcelingMode = ref<ParcelingMode>('smart');
const parcelingModeOptions = [
  { title: 'None (use as-is)', value: 'none' },
  { title: 'Default (fixed chunks)', value: 'default' },
  { title: 'Smart (optimize by timestamps)', value: 'smart' },
];
const defaultParcelSizeDays = ref(7); // days
const maxSamplesPerRequest = ref(5000);
const safetyMargin = ref(0.9);

// Time range tabs
const timeRangeTab = ref('presets');

// Map config
const homeState = ref({
  loc: [40.044, -98.789] as LatLngPair,
  zoom: 4,
  t: null as number | null
});

// Test regions - centered around central US
interface TestRegion {
  id: string;
  name: string;
  size: string;
  geometry: RectBounds;
  color: string;
}

const predefinedRegions = ref<TestRegion[]>([
  {
    id: 'small',
    name: 'Small Region',
    size: '0.02° × 0.02°',
    geometry: {
      xmin: -98.8,
      ymin: 40.03,
      xmax: -98.78,
      ymax: 40.05
    },
    color: 'green'
  },
  {
    id: 'medium',
    name: 'Medium Region',
    size: '0.1° × 0.05°',
    geometry: {
      xmin: -98.85,
      ymin: 40.0,
      xmax: -98.75,
      ymax: 40.05
    },
    color: 'blue'
  },
  {
    id: 'large',
    name: 'Large Region',
    size: '1.25° × 1.0°',
    geometry: {
      xmin: -99.375,
      ymin: 39.55,
      xmax: -98.125,
      ymax: 40.55
    },
    color: 'orange'
  }
]);

const selectedRegion = ref<TestRegion | null>(null);

// Sample points display state
const showSamplePoints = ref<Record<string, boolean>>({
  small: false,
  medium: false,
  large: false
});

// Actual sample points display state
const showActualSamplePoints = ref<Record<string, boolean>>({
  small: false,
  medium: false,
  large: false
});

// Store sample locations for each region
const regionSampleLocations = ref<Record<string, Array<{ x: number; y: number }>>>({});

// Store actual sample locations from fetch results
const regionActualSampleLocations = ref<Record<string, Array<{ x: number; y: number }>>>({});

// Multi-marker composables for each region
const sampleMarkers: Record<string, ReturnType<typeof useMultiMarker>> = {};

// Multi-marker composables for actual sample points (red)
const actualSampleMarkers: Record<string, ReturnType<typeof useMultiMarker>> = {};

// Sampler for generating sample point locations
const sampler = ref<EsriSampler | null>(null);

// Preset time ranges
interface PresetTimeRange {
  id: string;
  name: string;
  description: string;
  ranges: { start: number; end: number }[];
  config?: TimeRangeConfig; // Optional config for complex patterns
}

// Test preset: August 2-4, 2023 with multiple times per day
// Some of these time windows may not have ESRI data
const testAugust2023Config: TimeRangeConfigMultiple = {
  type: 'multiple',
  dateRange: {
    start: new Date('2023-08-02T00:00:00.000Z'),
    end: new Date('2023-08-04T23:59:59.999Z')
  },
  years: undefined,
  months: undefined,
  weekdays: undefined,
  times: ['10 am', '12 pm', '2 pm', '4 pm', '6 pm'],
  toleranceHours: [0.5, 0.5]
};

// Generate the "All Available Data" preset with the specified pattern
const allDataConfig: TimeRangeConfigMultiple = {
  type: 'multiple',
  dateRange: {
    start: new Date('2023-01-01T00:00:00.000Z'),
    end: new Date('2025-12-31T23:59:59.999Z')
  },
  years: undefined,
  months: undefined,
  // [
  //   'January', 'February', 'March', 'April', 'May', 'June',
  //   'July', 'August', 'September', 'October', 'November', 'December'
  // ],
  weekdays: undefined,
  // [
  //   'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  // ],
  times: undefined,
  toleranceHours: [0.5, 0.5]
};

const manyTimeRangesConfig: TimeRangeConfigMultiple = {
  type: 'multiple',
  dateRange: {
    start: new Date('2023-01-01T00:00:00.000Z'),
    end: new Date('2025-12-31T23:59:59.999Z')
  },
  years: undefined,
  months: [
    'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ],
  weekdays: [
    'Sunday', 'Monday', 'Tuesday', 'Thursday', 'Friday', 'Saturday'
  ],
  times: ['9 am', '11am', '1pm', '3pm'],
  toleranceHours: [0.5, 0.5]
};

// Student-realistic request: Summer mornings with discrete times
const studentSummerMorningsConfig: TimeRangeConfigMultiple = {
  type: 'multiple',
  dateRange: {
    start: new Date('2023-01-01T00:00:00.000Z'),
    end: new Date('2025-12-31T23:59:59.999Z')
  },
  years: undefined,
  months: ['June', 'July', 'August'],
  weekdays: undefined,
  times: ['8 am', '9 am', '10 am'],
  toleranceHours: [0.5, 0.5]
};

// Student-realistic request with smart parceling: Summer mornings with time range
const studentSummerMorningsSmartConfig: TimeRangeConfigMultiple = {
  type: 'multiple',
  dateRange: {
    start: new Date('2023-01-01T00:00:00.000Z'),
    end: new Date('2025-12-31T23:59:59.999Z')
  },
  years: undefined,
  months: ['June', 'July', 'August'],
  weekdays: undefined,
  times: ['8 am-11 am'],
  toleranceHours: undefined
};





// Computed preset time ranges based on actual available data
// These depend on the parcel size setting for proper parceling
const presetTimeRanges = computed<PresetTimeRange[]>(() => {
  // Get the last available timestamp from esriTimesteps
  const lastTimestamp = esriTimesteps.value.length > 0 
    ? esriTimesteps.value[esriTimesteps.value.length - 1] 
    : Date.now();
  
  const end = new Date(lastTimestamp);
  
  // Use defaultParcelSizeDays converted to milliseconds
  const parcelSizeMs = defaultParcelSizeDays.value * 24 * 60 * 60 * 1000;
  
  // Helper to create range configs with parceling
  const createRangeConfig = (daysBack: number): TimeRangeConfigMultiple => ({
    type: 'multiple',
    dateRange: {
      start: new Date(lastTimestamp - daysBack * 24 * 60 * 60 * 1000),
      end: end
    },
    years: undefined,
    months: undefined,
    weekdays: undefined,
    times: undefined,
    toleranceHours: undefined,
    parcel: false,
    parcelSize: parcelSizeMs
  });
  
  const configs = {
    lastWeek: createRangeConfig(7),
    lastMonth: createRangeConfig(30),
    last3Months: createRangeConfig(90),
    lastYear: createRangeConfig(365),
  };
  
  return [
    {
      id: 'last-week',
      name: 'Last Week',
      description: `Last 7 days of data`,
      ranges: generateTimeRanges(configs.lastWeek),
      config: configs.lastWeek
    },
    {
      id: 'last-month',
      name: 'Last Month',
      description: `Last 30 days of data`,
      ranges: generateTimeRanges(configs.lastMonth),
      config: configs.lastMonth
    },
    {
      id: 'last-3-months',
      name: 'Last 3 Months',
      description: `Last 90 days of data`,
      ranges: generateTimeRanges(configs.last3Months),
      config: configs.last3Months
    },
    {
      id: 'last-year',
      name: 'Last Year',
      description: `Last 365 days of data`,
      ranges: generateTimeRanges(configs.lastYear),
      config: configs.lastYear
    },
    {
      id: 'test-august-2023',
      name: 'Test: Aug 2-4, 2023',
      description: `3 days, 5 times/day (10am, 12pm, 2pm, 4pm, 6pm) ±30min`,
      ranges: generateTimeRanges({...testAugust2023Config, parcel: false, parcelSize: parcelSizeMs}, false),
      config: {...testAugust2023Config, parcel: false, parcelSize: parcelSizeMs}
    },
    {
      id: 'all-data-pattern',
      name: 'All Available Data (Pattern)',
      description: `2023-2025, All months/days`,
      ranges: filterForAllowedDates(generateTimeRanges({...allDataConfig, parcel: false, parcelSize: parcelSizeMs}, false), uniqueDays.value),
      config: {...allDataConfig, parcel: false, parcelSize: parcelSizeMs}
    },
    {
      id: 'many-ranges-pattern',
      name: 'Many Time Ranges (Pattern)',
      description: `2023-2025, Most months/days, 12pm/3pm/9pm ±30min`,
      ranges: filterForAllowedDates(generateTimeRanges({...manyTimeRangesConfig, parcel: false, parcelSize: parcelSizeMs}, false), uniqueDays.value),
      config: {...manyTimeRangesConfig, parcel: false, parcelSize: parcelSizeMs}
    },
    {
      id: 'student-summer-mornings',
      name: 'Student Request: Summer Mornings',
      description: `2023-2025, Jun-Aug, 8am/9am/10am ±30min`,
      ranges: filterForAllowedDates(generateTimeRanges({...studentSummerMorningsConfig, parcel: false, parcelSize: parcelSizeMs}, false), uniqueDays.value),
      config: {...studentSummerMorningsConfig, parcel: false, parcelSize: parcelSizeMs}
    },
    {
      id: 'student-summer-mornings-smart',
      name: 'Student Request: Summer Mornings (Smart)',
      description: `2023-2025, Jun-Aug, 8am-11am (time range)`,
      ranges: filterForAllowedDates(generateTimeRanges({...studentSummerMorningsSmartConfig, parcel: false, parcelSize: parcelSizeMs}, false), uniqueDays.value),
      config: {...studentSummerMorningsSmartConfig, parcel: false, parcelSize: parcelSizeMs}
    }
  ];
});

const selectedTimeRange = ref<PresetTimeRange | null>(null);

// Store custom time ranges created by the user
const customTimeRanges = ref<PresetTimeRange[]>([]);

// Computed: all available time ranges (presets + custom)
const allTimeRanges = computed(() => [...presetTimeRanges.value, ...customTimeRanges.value]);

// Computed: Actual sample count after grid adjustment
const actualSampleCount = computed(() => {
  if (!selectedRegion.value || !sampler.value) {
    return sampleCount.value;
  }
  
  // Set the geometry for the selected region
  sampler.value.setGeometry(selectedRegion.value.geometry);
  
  // Get the actual sample count that will be used
  const spec = sampler.value.getSamplingSpecificationFromSampleCount(sampleCount.value);
  return spec.count;
});

const sampleCountAdjustmentInfo = computed(() => {
  if (!selectedRegion.value || !sampler.value) {
    return null;
  }
  
  const actual = actualSampleCount.value;
  const requested = sampleCount.value;
  
  if (actual === requested) {
    return null; // No adjustment needed
  }
  
  // Get grid dimensions to explain the adjustment
  sampler.value.setGeometry(selectedRegion.value.geometry);
  const spec = sampler.value.getSamplingSpecificationFromSampleCount(requested);
  
  if (actual < requested) {
    return {
      type: 'reduced' as const,
      message: `Grid constraint: ${spec.lons} × ${spec.lats} = ${actual} samples`,
      reason: 'TEMPO grid spacing cannot accommodate the requested sample count'
    };
  } else {
    return {
      type: 'increased' as const,
      message: `Grid adjusted: ${spec.lons} × ${spec.lats} = ${actual} samples`,
      reason: 'Rounded up to fit TEMPO grid spacing'
    };
  }
});

// Computed
const canFetch = computed(() => {
  return selectedRegion.value !== null && selectedTimeRange.value !== null;
});

// Provide a safe current date for DateTimeRangeSelection
// Use the local singleDateSelected from useUniqueTimeSelection
const currentDate = computed(() => {
  const date = singleDateSelected.value;
  // Check if it's a valid Date object
  if (date && date instanceof Date && !isNaN(date.getTime())) {
    return date;
  }
  // Fallback to current date
  return new Date();
});

// Computed properties for Plotly graph
const plotlyDatasets = computed<PlotltGraphDataSet[]>(() => {
  const result: PlotltGraphDataSet[] = [];
  
  // Convert user datasets to plotly format
  datasets.value.forEach(dataset => {
    result.push(userDatasetToPlotly(dataset));
  });
  
  // Add failed request indicators
  const failedStats = requestStats.value.filter(stat => stat.status === 'error');
  console.log(`plotlyDatasets: Found ${failedStats.length} failed requests out of ${requestStats.value.length} total`);
  
  if (failedStats.length > 0) {
    // Calculate mean value from successful data points for y-position
    const allValues: number[] = [];
    datasets.value.forEach(dataset => {
      Object.values(dataset.samples || {}).forEach(sample => {
        if (sample.value !== null && !isNaN(sample.value)) {
          allValues.push(sample.value);
        }
      });
    });
    const meanValue = nanmean(allValues) ?? 1e16;
    console.log(`plotlyDatasets: Using mean value ${meanValue} for failed request lines, based on ${allValues.length} data points`);
    
    // Create red line segments for each failed time range
    failedStats.forEach((stat, idx) => {
      if (stat.timeRange) {
        const line = {
          x: [new Date(stat.timeRange.start), new Date(stat.timeRange.end)],
          y: [meanValue, meanValue],
          name: `Failed Request ${idx + 1}`,
          datasetOptions: {
            mode: 'lines',
            line: {
              color: 'red',
              width: 3,
              dash: 'solid'
            },
            showlegend: false,
            hovertemplate: `Failed Request<br>Time: %{x}<br>Error: ${stat.errorMessage || stat.errorType || 'Unknown'}<extra></extra>`
          }
        };
        result.push(line);

      }
    });
  }
  
  console.log(`plotlyDatasets: Returning ${result.length} total datasets (${result.length - datasets.value.length} failed request lines)`);
  return result;
});

const plotlyColors = computed(() => {
  return datasets.value.map(d => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (d as any).region?.color || 'blue';
  });
});

// Methods
function onMapReady(mapInstance: Map) {
  map.value = mapInstance;
  console.log('Map ready');
  
  // Initialize the sampler with service metadata
  const service = store.getTempoDataService('no2' as MoleculeType);
  service.withMetadataCache().then(meta => {
    sampler.value = new EsriSampler(meta);
    console.log('EsriSampler initialized with metadata');
  });
  
  // Initialize sample markers for each region (expected locations)
  predefinedRegions.value.forEach(region => {
    sampleMarkers[region.id] = useMultiMarker(map as Ref<Map | null>, {
      label: `samples-${region.id}`,
      color: region.color,
      fillColor: region.color,
      fillOpacity: 0.6,
      opacity: 1,
      radius: 4,
      outlineColor: '#ffffff',
      scale: 'screen'
    });
    
    // Initialize actual sample markers (red)
    actualSampleMarkers[region.id] = useMultiMarker(map as Ref<Map | null>, {
      label: `actual-samples-${region.id}`,
      color: '#ff0000',
      fillColor: '#ff0000',
      fillOpacity: 0.8,
      opacity: 1,
      radius: 3,
      outlineColor: '#ffffff',
      scale: 'screen'
    });
  });
  
  // Draw all predefined regions on the map
  drawRegionsOnMap();
}

function drawRegionsOnMap() {
  if (!map.value) return;
  
  predefinedRegions.value.forEach(region => {
    // Add region as a rectangle on the map
    if (map.value?.getSource(`region-${region.id}`)) {
      return; // Already added
    }
    
    map.value?.addSource(`region-${region.id}`, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [[
            [region.geometry.xmin, region.geometry.ymin],
            [region.geometry.xmax, region.geometry.ymin],
            [region.geometry.xmax, region.geometry.ymax],
            [region.geometry.xmin, region.geometry.ymax],
            [region.geometry.xmin, region.geometry.ymin]
          ]]
        },
        properties: {}
      }
    });
    
    map.value?.addLayer({
      id: `region-${region.id}-fill`,
      type: 'fill',
      source: `region-${region.id}`,
      paint: {
        'fill-color': region.color,
        'fill-opacity': 0.2
      }
    });
    
    map.value?.addLayer({
      id: `region-${region.id}-outline`,
      type: 'line',
      source: `region-${region.id}`,
      paint: {
        'line-color': region.color,
        'line-width': 2
      }
    });
  });
}

function selectRegion(region: TestRegion) {
  selectedRegion.value = region;
  
  // Update map opacity to highlight selected region
  if (map.value) {
    predefinedRegions.value.forEach(r => {
      const opacity = r.id === region.id ? 0.4 : 0.1;
      const lineWidth = r.id === region.id ? 3 : 2;
      map.value?.setPaintProperty(`region-${r.id}-fill`, 'fill-opacity', opacity);
      map.value?.setPaintProperty(`region-${r.id}-outline`, 'line-width', lineWidth);
    });
    
    // Fly to the selected region
    const centerLng = (region.geometry.xmin + region.geometry.xmax) / 2;
    const centerLat = (region.geometry.ymin + region.geometry.ymax) / 2;
    map.value.flyTo({
      center: [centerLng, centerLat],
      zoom: region.id === 'large' ? 7 : region.id === 'medium' ? 9 : 11,
      duration: 1000
    });
  }
  
  // Hide sample points from all other regions
  predefinedRegions.value.forEach(r => {
    if (r.id !== region.id && showSamplePoints.value[r.id]) {
      showSamplePoints.value[r.id] = false;
      const marker = sampleMarkers[r.id];
      if (marker) {
        marker.clearMarkers();
      }
    }
  });
  
  // Automatically show expected sample points for this region
  showSamplePoints.value[region.id] = true;
  toggleSamplePoints(region);
}

function selectTimeRange(preset: PresetTimeRange) {
  selectedTimeRange.value = preset;
}

function handleCustomTimeRangeChange(
  ranges: MillisecondRange[],
  selectionType: string,
  customName: string,
  config: TimeRangeConfig
) {
  // Create a new custom time range from the selection
  const customRange: PresetTimeRange = {
    id: `custom-${Date.now()}`,
    name: customName || `Custom Range ${customTimeRanges.value.length + 1}`,
    description: selectionType === 'single' 
      ? 'Single date selection' 
      : `Pattern: ${ranges.length} time range(s)`,
    ranges: ranges,
    config: config
  };
  
  // Add to custom ranges
  customTimeRanges.value.push(customRange);
  
  // Automatically select this new range
  selectedTimeRange.value = customRange;
  
  // Switch to presets tab to show it's selected
  timeRangeTab.value = 'presets';
  
  console.log(`Custom time range created: ${customRange.name} with ${ranges.length} ranges`);
}

function removeCustomTimeRange(id: string) {
  const index = customTimeRanges.value.findIndex(r => r.id === id);
  if (index >= 0) {
    customTimeRanges.value.splice(index, 1);
    // If this was selected, clear selection
    if (selectedTimeRange.value?.id === id) {
      selectedTimeRange.value = null;
    }
  }
}

function toggleSamplePoints(region: TestRegion) {
  if (!map.value) return;
  
  const show = showSamplePoints.value[region.id];
  const marker = sampleMarkers[region.id];
  
  if (!marker) {
    console.warn(`No marker initialized for region ${region.id}`);
    return;
  }
  
  if (show) {
    // Always regenerate sample points to reflect current sample count
    if (!sampler.value) {
      console.warn('Sampler not initialized yet. Please wait for metadata to load.');
      showSamplePoints.value[region.id] = false; // Reset checkbox
      return;
    }
    
    // Set the geometry for this region
    sampler.value.setGeometry(region.geometry);
    
    // Get sample locations based on the current sample count setting
    const locations = sampler.value.getSampleLocationsGrid(sampleCount.value);
    
    // Update the stored locations
    regionSampleLocations.value[region.id] = locations;
    
    marker.addMarkers(locations);
    console.log(`Showing ${locations.length} expected sample points for ${region.name} (sample count: ${sampleCount.value})`);
  } else {
    // Hide sample points
    marker.clearMarkers();
    console.log(`Hiding expected sample points for ${region.name}`);
  }
}

function toggleActualSamplePoints(region: TestRegion) {
  if (!map.value) return;
  
  const show = showActualSamplePoints.value[region.id];
  const marker = actualSampleMarkers[region.id];
  
  if (!marker) {
    console.warn(`No actual marker initialized for region ${region.id}`);
    return;
  }
  
  if (show) {
    // Get actual sample locations from fetch results
    const locations = regionActualSampleLocations.value[region.id];
    
    if (!locations || locations.length === 0) {
      console.warn(`No actual sample locations available for ${region.name}. Fetch data first.`);
      showActualSamplePoints.value[region.id] = false; // Reset checkbox
      return;
    }
    
    marker.addMarkers(locations);
    console.log(`Showing ${locations.length} actual sample points (red) for ${region.name}`);
  } else {
    // Hide actual sample points
    marker.clearMarkers();
    console.log(`Hiding actual sample points for ${region.name}`);
  }
}

async function fetchData() {
  if (!canFetch.value || !selectedRegion.value || !selectedTimeRange.value) return;
  
  fetching.value = true;
  
  // Clear previous stats before starting new fetch
  requestStats.value = [];
  
  // Reset progress tracking
  completedRequests.value = 0;
  
  // Track overall timing
  const overallStartTime = performance.now();
  
  try {
    const service = store.getTempoDataService('no2' as MoleculeType);
    await service.withMetadataCache();
    
    // Set rate limit and retry limit from UI controls
    service.setRateLimit(rateLimitMs.value);
    service.setRetryLimit(maxRetries503.value);
    
    // Configure parceling settings
    service.setParcelingMode(parcelingMode.value);
    service.setDefaultParcelSize(defaultParcelSizeDays.value * 24 * 60 * 60 * 1000); // Convert days to ms
    service.setMaxSamplesPerRequest(maxSamplesPerRequest.value);
    service.setSafetyMargin(safetyMargin.value);
    
    // Pass in the available timestamps for smart parceling
    service.setAvailableTimestamps(esriTimesteps.value);
    
    // Use the selected time range (either preset or custom)
    // The ranges are already chunked via the parcel option in generateTimeRanges
    const timeRanges: { start: number; end: number }[] = selectedTimeRange.value.ranges;
    
    // Set total requests for progress tracking
    totalRequests.value = timeRanges.length;
    
    // Log sample count information
    const adjustmentInfo = sampleCountAdjustmentInfo.value;
    if (adjustmentInfo) {
      console.log(`%c⚠️  Sample Count Adjusted`, 'color: #FF9800; font-weight: bold; font-size: 12px');
      console.log(`   Requested: ${sampleCount.value}`);
      console.log(`   Actual:    ${actualSampleCount.value}`);
      console.log(`   Reason:    ${adjustmentInfo.reason}`);
      console.log(`   Grid:      ${adjustmentInfo.message}`);
    } else {
      console.log(`Using sample count: ${sampleCount.value}`);
    }
    
    console.log(`Fetching data for ${timeRanges.length} time chunks`);
    
    // Create progress callback
    const onProgress = (stats: RequestStats, completed: number, _total: number) => {
      // Add stats in real-time
      requestStats.value.push(stats);
      completedRequests.value = completed;
      // console.log(`Progress: ${completed}/${total} - ${stats.status === 'success' ? 'Success' : 'Error'}`);
    };
    
    // Fetch timeseries data with progress callback
    const result = await service.fetchTimeseriesData(
      selectedRegion.value.geometry,
      timeRanges,
      { 
        sampleCount: sampleCount.value,
        onProgress: onProgress
      }
    );
    
    // Stats are already added via the progress callback, no need to add them again
    console.log(`Fetch complete. Total stats collected: ${requestStats.value.length}`);
    
    // Store actual sample locations for this region (from fetched data)
    if (result.locations && result.locations.length > 0) {
      regionActualSampleLocations.value[selectedRegion.value.id] = result.locations;
      console.log(`Stored ${result.locations.length} actual sample locations for ${selectedRegion.value.name}`);
      
      // If actual sample points are currently showing for this region, update them
      if (showActualSamplePoints.value[selectedRegion.value.id]) {
        const marker = actualSampleMarkers[selectedRegion.value.id];
        if (marker) {
          marker.clearMarkers();
          marker.addMarkers(result.locations);
          console.log(`Updated displayed actual sample points (red) with fetched data locations`);
        }
      }
    }
    
    // Create dataset for display
    const dataset: UserDataset = {
      id: v4(),
      name: `${selectedRegion.value.name} - ${selectedTimeRange.value.name}`,
      samples: result.values,
      errors: result.errors,
      timeRange: {
        id: v4(),
        name: selectedTimeRange.value.name,
        description: selectedTimeRange.value.description,
        range: timeRanges.length === 1 ? timeRanges[0] : timeRanges,
        type: 'single'
      },
      region: {
        ...selectedRegion.value.geometry,
        id: selectedRegion.value.id,
        name: selectedRegion.value.name,
        color: selectedRegion.value.color
      },
      molecule: 'NO₂',
      locations: result.locations
    };
    
    datasets.value.push(dataset);
    
    console.log(`Fetched ${Object.keys(result.values).length} data points`);
    
    // ============================================================================
    // LOAD TEST SUMMARY - Comprehensive Statistics
    // ============================================================================
    const overallEndTime = performance.now();
    const totalDuration = overallEndTime - overallStartTime;
    
    // Calculate statistics from requestStats
    const allStats = requestStats.value;
    const successfulRequests = allStats.filter(s => s.status === 'success');
    const failedRequests = allStats.filter(s => s.status === 'error');
    const error503Count = allStats.filter(s => s.errorType === '503').length;
    const error400Count = allStats.filter(s => s.errorType === '400').length;
    const errorOtherCount = allStats.filter(s => s.errorType === 'other' && s.status === 'error').length;
    
    // Break down retried requests by success/failure
    const retriedRequests = allStats.filter(s => s.retried);
    const retriedSuccessful = retriedRequests.filter(s => s.status === 'success').length;
    const retriedFailed = retriedRequests.filter(s => s.status === 'error').length;
    const retriedFrom503Successful = allStats.filter(s => s.retriedFrom503 && s.status === 'success').length;
    const retriedFrom503Failed = allStats.filter(s => s.retriedFrom503 && s.status === 'error').length;
    
    // Timing statistics
    const durations = allStats.map(s => s.duration);
    const totalRequestTime = durations.reduce((sum, d) => sum + d, 0);
    const avgDuration = durations.length > 0 ? totalRequestTime / durations.length : 0;
    const minDuration = durations.length > 0 ? Math.min(...durations) : 0;
    const maxDuration = durations.length > 0 ? Math.max(...durations) : 0;
    
    // Sample statistics
    const totalSamplesFetched = successfulRequests.reduce((sum, s) => sum + s.sampleCount, 0);
    const expectedSamplesPerRequest = sampleCount.value;
    const actualTimeRangesFetched = allStats.length;
    const inputTimeRanges = timeRanges.length;
    
    // Data points - actual results returned
    const dataPointsReturned = Object.keys(result.values).length; // Unique timestamps in returned data
    const locationsCount = result.locations?.length || 0;
    
    // Print styled summary
    console.log('%c╔════════════════════════════════════════════════════════════════╗', 'color: #4CAF50; font-weight: bold');
    console.log('%c║                    LOAD TEST SUMMARY                          ║', 'color: #4CAF50; font-weight: bold');
    console.log('%c╚════════════════════════════════════════════════════════════════╝', 'color: #4CAF50; font-weight: bold');
    
    console.log('%c\n⏱️  TIMING', 'color: #2196F3; font-weight: bold; font-size: 14px');
    console.log(`   Total Duration:        ${(totalDuration / 1000).toFixed(2)}s`);
    console.log(`   Request Time (sum):    ${(totalRequestTime / 1000).toFixed(2)}s`);
    console.log(`   Avg Request Duration:  ${avgDuration.toFixed(0)}ms`);
    console.log(`   Min Request Duration:  ${minDuration.toFixed(0)}ms`);
    console.log(`   Max Request Duration:  ${maxDuration.toFixed(0)}ms`);
    
    console.log('%c\n📊 REQUEST STATISTICS', 'color: #FF9800; font-weight: bold; font-size: 14px');
    console.log(`   Input Time Ranges:     ${inputTimeRanges}`);
    console.log(`   Actual Requests Made:  ${actualTimeRangesFetched}`);
    console.log(`   Successful Requests:   ${successfulRequests.length} (${((successfulRequests.length / actualTimeRangesFetched) * 100).toFixed(1)}%)`);
    console.log(`   Failed Requests:       ${failedRequests.length} (${((failedRequests.length / actualTimeRangesFetched) * 100).toFixed(1)}%)`);
    
    if (retriedRequests.length > 0) {
      console.log(`   Retried Requests:      ${retriedRequests.length} total`);
      console.log(`     → Succeeded:         ${retriedSuccessful}`);
      console.log(`     → Failed:            ${retriedFailed}`);
      if (retriedFrom503Successful > 0 || retriedFrom503Failed > 0) {
        console.log(`   Retried from 503:      ${retriedFrom503Successful + retriedFrom503Failed} total`);
        console.log(`     → Succeeded:         ${retriedFrom503Successful}`);
        console.log(`     → Failed:            ${retriedFrom503Failed}`);
      }
    }
    
    if (failedRequests.length > 0) {
      console.log('%c\n⚠️  ERROR BREAKDOWN', 'color: #F44336; font-weight: bold; font-size: 14px');
      console.log(`   503 Errors:            ${error503Count}`);
      console.log(`   400 Errors:            ${error400Count}`);
      console.log(`   Other Errors:          ${errorOtherCount}`);
    }
    
    console.log('%c\n📈 SAMPLE STATISTICS', 'color: #9C27B0; font-weight: bold; font-size: 14px');
    console.log(`   Expected Samples/Time: ${expectedSamplesPerRequest}`);
    if (parcelingMode.value === 'smart' && result.expectedTotalSamples !== undefined) {
      // Use the actual expected total from the service (counts timestamps in time ranges)
      console.log(`   Expected Total Count:  ${result.expectedTotalSamples.toLocaleString()}`);
    }
    console.log(`   Total Samples Fetched: ${totalSamplesFetched.toLocaleString()}`);
    console.log(`   Avg Samples/Request:   ${actualTimeRangesFetched > 0 ? (totalSamplesFetched / actualTimeRangesFetched).toFixed(0) : 0}`);
    
    console.log('%c\n🎯 DATA RESULTS', 'color: #00BCD4; font-weight: bold; font-size: 14px');
    console.log(`   Data Points Returned:  ${dataPointsReturned} (unique timestamps)`);
    console.log(`   Unique Locations:      ${locationsCount}`);
    console.log(`   Total Data Values:     ${(dataPointsReturned * locationsCount).toLocaleString()}`);
    
    // Use expected total from service if available (smart mode)
    if (result.expectedTotalSamples !== undefined) {
      console.log(`   Expected Samples:      ${result.expectedTotalSamples.toLocaleString()}`);
      console.log(`   Actual Samples:        ${totalSamplesFetched.toLocaleString()}`);
      console.log(`   Sample Efficiency:     ${((totalSamplesFetched / result.expectedTotalSamples) * 100).toFixed(1)}%`);
    } else {
      // For non-smart modes, we don't have an accurate prediction
      console.log(`   Actual Samples:        ${totalSamplesFetched.toLocaleString()}`);
    }
    
    console.log('%c\n⚙️  CONFIGURATION', 'color: #607D8B; font-weight: bold; font-size: 14px');
    console.log(`   Parceling Mode:        ${parcelingMode.value}`);
    console.log(`   Sample Count:          ${sampleCount.value}`);
    console.log(`   Rate Limit:            ${rateLimitMs.value}ms`);
    console.log(`   Max Retries:           ${maxRetries503.value}`);
    if (parcelingMode.value === 'smart') {
      console.log(`   Max Samples/Request:   ${maxSamplesPerRequest.value}`);
      console.log(`   Safety Margin:         ${(safetyMargin.value * 100).toFixed(0)}%`);
    } else if (parcelingMode.value === 'default') {
      console.log(`   Parcel Size:           ${defaultParcelSizeDays.value} days`);
    }
    
    console.log('%c\n' + '═'.repeat(64), 'color: #4CAF50');
    
    // Add a fake failed request for testing visualization
    if (timeRanges.length > 0 && requestStats.value.length > 0) {
      // Pick a time range in the middle of the data
      const middleIndex = Math.floor(timeRanges.length / 2);
      const fakeFailedRange = timeRanges[middleIndex];
      
      const _fakeFailedStat: RequestStats = {
        status: 'error',
        statusCode: 400,
        errorType: '400',
        errorMessage: 'FAKE ERROR FOR TESTING: This is a simulated failed request to test visualization',
        duration: 1500,
        sampleCount: sampleCount.value,
        retried: false,
        timestamp: fakeFailedRange.start,
        timeRange: fakeFailedRange,
        url: 'https://fake-test-url.example.com'
      };
      
      // requestStats.value.push(fakeFailedStat);
      console.log('Added fake failed request for testing visualization');
    }
  } catch (error) {
    console.error('Error fetching data:', error);
    alert('Error fetching data. Check console for details.');
  } finally {
    fetching.value = false;
  }
}

// Generated by Copilot - Dry run function to generate URLs without fetching
async function dryRunData() {
  if (!canFetch.value || !selectedRegion.value || !selectedTimeRange.value) return;
  
  fetching.value = true;
  
  try {
    const service = store.getTempoDataService('no2' as MoleculeType);
    await service.withMetadataCache();
    
    // Configure parceling settings
    service.setParcelingMode(parcelingMode.value);
    service.setDefaultParcelSize(defaultParcelSizeDays.value * 24 * 60 * 60 * 1000);
    service.setMaxSamplesPerRequest(maxSamplesPerRequest.value);
    service.setSafetyMargin(safetyMargin.value);
    service.setAvailableTimestamps(esriTimesteps.value);
    
    // Enable dry run mode
    service.setDryRun(true);
    
    const timeRanges: { start: number; end: number }[] = selectedTimeRange.value.ranges;
    
    console.log('%c╔════════════════════════════════════════════════════════════════╗', 'color: #FF9800; font-weight: bold');
    console.log('%c║                      DRY RUN MODE                              ║', 'color: #FF9800; font-weight: bold');
    console.log('%c╚════════════════════════════════════════════════════════════════╝', 'color: #FF9800; font-weight: bold');
    console.log(`\nGenerating URLs for ${timeRanges.length} time chunks...`);
    
    // "Fetch" with dry run enabled
    await service.fetchTimeseriesData(
      selectedRegion.value.geometry,
      timeRanges,
      {
        sampleCount: actualSampleCount.value,
        dryRun: true
      }
    );
    
    // Get the generated URLs
    const urls = service.getRequestUrls();
    
    console.log('%c\n📋 GENERATED URLs:', 'color: #FF9800; font-weight: bold; font-size: 14px');
    console.log(`   Total URLs: ${urls.length}`);
    console.log('\nAll Request URLs (JSON):');
    console.log({url: urls});
    
    console.log('%c\n✅ Dry run complete - no requests were made', 'color: #4CAF50; font-weight: bold');
    
    // Disable dry run mode
    service.setDryRun(false);
    
    // alert(`Dry run complete! Generated ${urls.length} URLs.\nCheck console for details.`);
  } catch (error) {
    console.error('Error in dry run:', error);
    alert('Error in dry run. Check console for details.');
  } finally {
    fetching.value = false;
  }
}

// Note: splitTimeRanges is no longer needed - parceling is handled by generateTimeRanges

function clearStatistics() {
  requestStats.value = [];
  datasets.value = [];
  totalRequests.value = 0;
  completedRequests.value = 0;
  selectedTimeRangeForMap.value = null;
}

function handleTimeRangeSelect(stat: RequestStats) {
  selectedTimeRangeForMap.value = stat;
  
  // Update the map timestamp to show the selected time
  if (stat.timeRange) {
    // Set the map time to the start of the selected time range
    mapTimestamp.value = stat.timeRange.start;
    console.log(`Selected time range: ${new Date(stat.timeRange.start).toISOString()} - ${new Date(stat.timeRange.end).toISOString()}`);
  }
}

function handleGraphClick(point: {x: Date | number | string | null, y: number, customdata: unknown}) {
  console.log("handleGraphClick called with:", point);
  // Update the map timestamp when a point on the graph is clicked
  let timestamp: number | null = null;
  
  if (point.x instanceof Date) {
    timestamp = point.x.getTime();
  } else if (typeof point.x === 'number') {
    timestamp = point.x;
  } else if (typeof point.x === 'string') {
    // Parse string date
    const parsed = new Date(point.x);
    if (!isNaN(parsed.getTime())) {
      timestamp = parsed.getTime();
    }
  }
  
  console.log("Processed timestamp:", timestamp, "from point.x type:", typeof point.x);
  if (timestamp) {
    mapTimestamp.value = timestamp;
    console.log(`Graph point clicked: ${new Date(timestamp).toISOString()}, value: ${point.y}`);
  } else {
    console.warn("Could not extract timestamp from point.x:", point.x);
  }
}

function jitterRegions() {
  // Add small random offset to each region's bounds
  // Jitter amount: ±0.001 to ±0.005 degrees (roughly 100-500 meters)
  const jitterAmount = 0.1;
  
  predefinedRegions.value.forEach(region => {
    const dx = (Math.random() - 0.5) * 2 * jitterAmount;
    const dy = (Math.random() - 0.5) * 2 * jitterAmount;
    
    region.geometry.xmin += dx;
    region.geometry.xmax += dx;
    region.geometry.ymin += dy;
    region.geometry.ymax += dy;
    
    console.log(`Jittered ${region.name} by (${dx.toFixed(5)}, ${dy.toFixed(5)})`);
  });
  
  // Clear expected sample locations since regions have moved
  regionSampleLocations.value = {};
  // Note: Keep actual sample locations - they're from real data
  
  // Update map visualization
  if (map.value) {
    predefinedRegions.value.forEach(region => {
      const source = map.value?.getSource(`region-${region.id}`);
      if (source && 'setData' in source && typeof source.setData === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (source as any).setData({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[
              [region.geometry.xmin, region.geometry.ymin],
              [region.geometry.xmax, region.geometry.ymin],
              [region.geometry.xmax, region.geometry.ymax],
              [region.geometry.xmin, region.geometry.ymax],
              [region.geometry.xmin, region.geometry.ymin]
            ]]
          },
          properties: {}
        });
      }
    });
  }
  
  console.log('Regions jittered for cache busting');
}

// Watch for parcel size changes and update selected preset if needed
watch(defaultParcelSizeDays, () => {
  // If a preset time range is selected, update it with the new parcel size
  if (selectedTimeRange.value) {
    const presetId = selectedTimeRange.value.id;
    
    // Check if this is a preset (not a custom range)
    const updatedPreset = presetTimeRanges.value.find(p => p.id === presetId);
    
    if (updatedPreset) {
      // Update the selected range with the newly generated ranges
      selectedTimeRange.value = updatedPreset;
      console.log(`Updated selected time range "${updatedPreset.name}" with ${updatedPreset.ranges.length} ranges for parcel size: ${defaultParcelSizeDays.value} days`);
    }
  }
});

// Watch for sample count changes and update displayed sample points automatically
watch(sampleCount, () => {
  // Update all visible sample point displays
  predefinedRegions.value.forEach(region => {
    if (showSamplePoints.value[region.id]) {
      // Regenerate and update the displayed points
      if (sampler.value) {
        sampler.value.setGeometry(region.geometry);
        const locations = sampler.value.getSampleLocationsGrid(sampleCount.value);
        regionSampleLocations.value[region.id] = locations;
        
        const marker = sampleMarkers[region.id];
        if (marker) {
          marker.clearMarkers();
          marker.addMarkers(locations);
          console.log(`Updated sample points for ${region.name}: ${locations.length} locations (sample count: ${sampleCount.value})`);
        }
      }
    }
  });
});

// Note: Timestamps are now fetched directly via useEsriTimesteps composable
// No need for onMounted initialization
</script>

<style scoped>
.map-container {
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
}

.v-chip--active {
  border-width: 2px !important;
  font-weight: bold;
}
</style>
